import { useWidgetContext } from 'widgets/WidgetContext.tsx';
import { useVolumeCurveConfigSlice } from 'widgets/volumeCurve/store/configuration/configSlice.ts';
import { useMemo } from 'react';
import { useSelectLocked } from 'layoutRenderer/store/selectors.ts';
import { VolumeCurveConfiguration } from 'widgets/volumeCurve/store/configuration/VolumeCurveConfiguration.ts';
import { useWidgetPortfolioSelectionStatus } from 'widgets/hooks.ts';
import styleConstants from 'styles/variables.module.scss';

const legendsMarginTop = 2;
const legendRowHeight = 18;
const padding = 2 * parseInt(styleConstants.spaceAround);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

export const useVolumeCurveDimensions = () => {
    const { height, width } = useWidgetContext();
    const { config } = useVolumeCurveConfigSlice();
    const isLocked = useSelectLocked();
    const { hasValidSelection } = useWidgetPortfolioSelectionStatus();

    return useMemo(() => {
        const hideToolbar = !!config?.hideToolbar || !hasValidSelection;
        const toolbarHeight = hideToolbar ? 0 : 16;

        const toolbarConsumedHeight = hideToolbar ? 0 : toolbarHeight;
        const bodyHeight = hideToolbar
            ? height - borderWidth - padding
            : height - toolbarConsumedHeight - borderWidth - padding;

        const visibleLegendRows = getVisibleLegendRows(config, isLocked);
        const legendHeight = visibleLegendRows * legendRowHeight;

        const chartHeight = bodyHeight - legendHeight;

        return {
            bodyHeight,
            chartHeight,
            width,
            height,
            legendsMarginTop,
        };
    }, [hasValidSelection, width, height, config, isLocked]);
};

function getVisibleLegendRows(
    config: VolumeCurveConfiguration,
    isLocked: boolean
) {
    if (!isLocked) {
        return 2;
    }
    const {
        hideHistoricalVolumeExcludingAuctionLegend,
        hideTradedVolumeLegend,
        hideAuctionVolumeLegend,
        hideCumulativeHistoricalVolumeLegend,
        hideCumulativeTradedVolumeLegend,
    } = config;

    const showRow1 =
        hideHistoricalVolumeExcludingAuctionLegend &&
        hideCumulativeHistoricalVolumeLegend &&
        hideAuctionVolumeLegend
            ? 0
            : 1;
    const showRow2 =
        hideTradedVolumeLegend && hideCumulativeTradedVolumeLegend ? 0 : 1;

    return showRow1 + showRow2;
}
